import { useEffect, memo, useState, useRef, useMemo } from "react";
import _ from "lodash";
import _isEmpty from "lodash/isEmpty";
import { Layout, Menu, Image, Skeleton, Badge } from "antd";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import LogoIcon from "assets/images/vg_logo_small.svg";
import { getCountNotSeenRequest } from "providers/NewsProvider/slice";
import { getCountNotReadSupportRequest } from "providers/SupportProvider/slice";
import { toggleCollapseMenu } from "providers/GeneralProvider/slice";
import { useSocket } from "hooks";
import "./styles.less";
import { ROLES } from "utils/constants";
import MenuRoutes from "./SideMenuRoutes";

const { Sider } = Layout;

const CustomSideMenu = ({ width, collapsed }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const [unreadConversations, setUnreadConversations] = useState({
    FONEHOUSE_TO_USER: 0,
    VIETGO_TO_USER: 0,
    CONSULATE_TO_USER: 0,
  });
  const userRoles = useSelector((state) =>
    _.get(state, "auth.currentUser.roles", [])
  );
  const userRole = useSelector((state) =>
    _.get(state, "auth.currentUser.role")
  );
  const roleChat = useSelector((state) =>
    _.get(state, "auth.currentUser.roleChat")
  );
  const unreadNews = useSelector((state) => _.get(state, "news.unreadNews"));
  const currentUser = useSelector((state) => _.get(state, "auth.currentUser"));
  const currentUserId = _.get(currentUser, "_id");
  const allSupportUnread = useSelector((state) =>
    _.get(state, "support.allSupportUnread")
  );
  const totalRating = useSelector((state) =>
    _.get(state, "ratingComment.totalRating")
  );
  const totalReport = useSelector((state) =>
    _.get(state, "reportManager.totalReport")
  );

  const canGetCountNotReadSupport = [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(
    userRole
  );
  const canGetCountNotSeen = userRoles.includes("news");
  const { socket, isConnected } = useSocket();
  const unreadRef = useRef(unreadConversations);
  unreadRef.current = unreadConversations;

  // Fetch unread counts via socket
  useEffect(() => {
    if (currentUserId && socket && isConnected) {
      socket.emit("subscribe", currentUserId);
      socket.emit("unread-conversations", {
        room: currentUserId,
      });

      const handleUnreadConversations = (data) => {
        const newUnread = _.get(data, "unreadConversations", {});
        setUnreadConversations((prev) => {
          if (
            prev.FONEHOUSE_TO_USER === newUnread.FONEHOUSE_TO_USER &&
            prev.VIETGO_TO_USER === newUnread.VIETGO_TO_USER &&
            prev.CONSULATE_TO_USER === newUnread.CONSULATE_TO_USER
          ) {
            return prev;
          }
          return newUnread;
        });
      };

      const handleReconnect = () => {
        socket.emit("subscribe", currentUserId);
        socket.emit("unread-conversations", {
          room: currentUserId,
        });
      };

      socket.on("unread-conversations", handleUnreadConversations);
      socket.on("connect", handleReconnect);
      return () => {
        socket.off("unread-conversations", handleUnreadConversations);
        socket.off("connect", handleReconnect);
      };
    }
  }, [currentUserId, socket, isConnected]);

  useEffect(() => {
    if (canGetCountNotSeen) {
      dispatch(getCountNotSeenRequest());
    }
  }, [dispatch, canGetCountNotSeen]);

  useEffect(() => {
    if (canGetCountNotReadSupport) {
      dispatch(getCountNotReadSupportRequest());
    }
  }, [dispatch, canGetCountNotReadSupport]);

  const getUnreadCount = (menuKey) => {
    switch (menuKey) {
      case "news":
        return unreadNews;
      case "comment_and_rating":
        return totalRating;
      case "support":
        return allSupportUnread;
      case "cms_admin_report_manager":
        return totalReport;
      case "messengers":
        return unreadConversations.FONEHOUSE_TO_USER;
      case "viego_messengers":
        return unreadConversations.VIETGO_TO_USER;
      case "consulate_messengers":
        return unreadConversations.CONSULATE_TO_USER;
      default:
        return 0;
    }
  };

  const getParentBadge = (parentKey) => {
    if (parentKey === "fonehouse" || parentKey === "/fonehouse") {
      return unreadConversations.FONEHOUSE_TO_USER || 0;
    }
    if (parentKey === "cms_viego_support") {
      return unreadConversations.VIETGO_TO_USER || 0;
    }
    if (parentKey === "consulate") {
      return unreadConversations.CONSULATE_TO_USER || 0;
    }
    return 0;
  };

  const isMenuItemVisible = (menu) => {
    if (
      ["/liquid_viego_manager"].includes(menu._to) &&
      userRole !== "super_admin"
    ) {
      return false;
    }
    if (
      [
        "/fonehouse",
        "/registerBusiness",
        "/banners",
        "/support",
        "/cms_viego_support",
        "/voting",
        "/consulate",
      ].includes(menu._to) &&
      userRole === "user"
    ) {
      return false;
    }
    if (
      userRole === "user" &&
      !userRoles.includes(menu.key) &&
      [
        "news",
        "visa",
        "supermarket",
        "restaurant",
        "entertainment",
        "tutorial",
        "cosmetic",
        "sim",
        "travel",
        "shipper",
        "car-and-old-stuff",
        "jobs",
      ].includes(menu.key)
    ) {
      return false;
    }
    if (
      userRole === "admin" &&
      !userRoles.includes(menu.key) &&
      ["cms_admin", "cms_admin_manager"].includes(menu.key)
    ) {
      return false;
    }
    if (
      userRole !== "super_admin" &&
      userRole !== "admin" &&
      [
        "cms_admin",
        "cms_admin_manager",
        "cms_viego_support",
        "reward_point_manager",
        "user_transactions",
        "cms_admin_report_manager",
        "comment_and_rating",
        "identify_user",
        "system_notifications",
      ].includes(menu.key)
    ) {
      return false;
    }
    if (
      userRole !== "super_admin" &&
      !roleChat &&
      (menu.key === "messengers" || menu.key === "viego_messengers")
    ) {
      return false;
    }
    return true;
  };

  const renderLabel = (text, menuKey) => {
    const count = getUnreadCount(menuKey);
    if (count > 0) {
      return (
        <span className="menu-label-with-badge">
          {text}
          <Badge count={count} size="small" />
        </span>
      );
    }
    return text;
  };

  const menuItems = useMemo(() => {
    if (_isEmpty(currentUser)) return [];

    const buildItems = (routes) => {
      const items = [];
      routes.forEach((menu) => {
        if (!isMenuItemVisible(menu)) return;

        if (_.has(menu, "subMenu")) {
          const parentKey = menu.key || menu._to;
          const parentBadge = getParentBadge(parentKey);
          const children = buildItems(menu.subMenu);
          if (children.length > 0 || menu.subMenu.length > 0) {
            items.push({
              key: parentKey,
              icon: menu.icon,
              label: parentBadge > 0 ? (
                <span className="menu-label-with-badge">
                  {menu.text}
                  <Badge count={parentBadge} size="small" />
                </span>
              ) : (
                menu.text
              ),
              disabled: menu.disabled,
              children,
            });
          }
        } else {
          items.push({
            key: menu._to,
            icon: menu.icon,
            label: renderLabel(menu.text, menu.key),
            disabled: menu.disabled,
          });
        }
      });
      return items;
    };

    return buildItems(MenuRoutes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentUser,
    userRole,
    userRoles,
    roleChat,
    unreadNews,
    allSupportUnread,
    totalRating,
    totalReport,
    unreadConversations,
  ]);

  const splitLocationArr = pathname
    .split("/")
    .reduce((splitLocation, route, index) => {
      if (index > 0) {
        splitLocation.push(`/${route}`);
      }
      return splitLocation;
    }, []);

  const onMenuItemClick = ({ keyPath }) => {
    const revkeyPath = [...keyPath].reverse();
    const pathString = revkeyPath.join("");
    if (pathString !== pathname) {
      navigate(pathString);
    }
    if (window.innerWidth <= 1024) {
      dispatch(toggleCollapseMenu(false));
    }
  };

  return (
    <Sider
      width={width}
      className="aside-menu"
      trigger={null}
      collapsible
      collapsed={collapsed}
      collapsedWidth={80}
      theme="light"
    >
      <div className="side-menu-logo">
        <Image src={LogoIcon} width={collapsed ? 36 : 42} height={collapsed ? 36 : 42} preview={false} />
        {!collapsed && <div className="logo-text">Vie-Go CMS</div>}
      </div>
      {!_isEmpty(currentUser) ? (
        <Menu
          mode="inline"
          selectedKeys={splitLocationArr}
          defaultOpenKeys={collapsed ? [] : splitLocationArr}
          style={{ borderRight: 0 }}
          onClick={onMenuItemClick}
          items={menuItems}
        />
      ) : (
        <Skeleton style={{ padding: 16 }} />
      )}
    </Sider>
  );
};

CustomSideMenu.propTypes = {
  collapsed: PropTypes.bool,
  width: PropTypes.number,
};

export default memo(CustomSideMenu);
